import { useGet, usePost, useQueryWithErrorHandling, useMutationWithErrorHandling } from '@app/sdk';
import { useQueryClient } from '@tanstack/react-query';
import { BillingPeriodBalance, BNPL } from '../../models/ledger';

function useLedgerService() {
  const get = useGet(true, "ams");
  const post = usePost(true, "ams");

  function getLedger(accountId: string): Promise<Ledger> {
    return get<Ledger>(`/api/proxy/ams/ledger-service/account/${accountId}/ledger`);
  }

  function getBillingPeriodBalance(accountId: string, billingPeriodId: number) {
    return get<BillingPeriodBalance>(
      `/api/proxy/ams/ledger-service/account/${accountId}/billingperiod/${billingPeriodId}/balance`
    );
  }

  function getUnassignedEntries(accountId: string) {
    return get<{
      transactions: any[];
    }>(`/api/proxy/ams/ledger-service/account/${accountId}/billingperiod/unassigned-entries`).then((response) => {
      const ledgerEntries = response.transactions.map((tx) => {
        return {
          ledgerEntryId: tx.transactionId,
          ...tx,
        };
      });

      return {
        ledgerEntries,
      };
    });
  }

  function getBillingPeriodLedgerEntries(accountId: string, billingPeriodId: number) {
    return get(`/api/proxy/ams/ledger-service/account/${accountId}/billingPeriod/${billingPeriodId}/entries`);
  }

  function getLedgerAllocations(accountId: string, transactionId: string) {
    return get(`/api/proxy/ams/ledger-service/account/${accountId}/transaction/${transactionId}`);
  }

  function getBNPLs(accountId: string) {
    return get<{ bnpLs: BNPL[] }>(`/api/proxy/ams/ledger-service/bnpl/account/${accountId}`).then((x) => x.bnpLs);
  }

  function cancelBNPL(accountId: string, subledgerId: string) {
    return post(`/api/proxy/ams/ledger-service/bnpl/account/${accountId}/subledger/${subledgerId}/cancel`, {});
  }

  return {
    getLedger,
    getBillingPeriodBalance,
    getUnassignedEntries,
    getBillingPeriodLedgerEntries,
    getLedgerAllocations,
    getBNPLs,
    cancelBNPL
  };
}

export function useGetLedger({ accountId }: { accountId: string }) {
  const { getLedger } = useLedgerService();
  return useQueryWithErrorHandling({
    queryKey: ['ledger', { accountId }],
    queryFn: () => getLedger(accountId),
    enabled: !!accountId,
    silent404: true,
  });
}

export function useRefreshLedger() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: ['ledger'],
    });
  };
}

export function useGetBillingPeriodBalance({
  accountId,
  billingPeriodId,
}: {
  accountId: string;
  billingPeriodId: number;
}) {
  const { getBillingPeriodBalance } = useLedgerService();
  return useQueryWithErrorHandling({
    queryKey: ['billingPeriodBalance', accountId, billingPeriodId],
    queryFn: () => getBillingPeriodBalance(accountId, billingPeriodId),
    enabled: !!accountId && !!billingPeriodId,
  });
}

export function useGetBNPLs(accountId: string) {
  const { getBNPLs } = useLedgerService();
  return useQueryWithErrorHandling({
    queryKey: ['bnpls', accountId],
    queryFn: () => getBNPLs(accountId),
    enabled: !!accountId,
  });
}

export function useCancelBNPL() {
  const { cancelBNPL } = useLedgerService();
  return useMutationWithErrorHandling({
    mutationFn: ({ accountId, subledgerId }: { accountId: string; subledgerId: string }) =>
      cancelBNPL(accountId, subledgerId),
  });
}